import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import BookImage from '../components/BookImage';
import BookModal from '../components/BookModal';
import { useAuth } from '../components/Auth/AuthContext';
import { useSubscription } from '../components/Auth/SubscriptionContext';
import { API_BASE_URL } from '../config/api';
import SubscriptionManagementModal from '../components/Auth/SubscriptionManagementModal';

function ShelfPage() {
  const [books, setBooks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [sortBy, setSortBy] = useState('dateAdded'); // 'dateAdded', 'title', 'author'
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [showBookModal, setShowBookModal] = useState(false);
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { subscription } = useSubscription();
  const navigate = useNavigate();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Fetch shelf books
  useEffect(() => {
    const fetchShelfBooks = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/shelf/books`, {
          credentials: 'include'
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch shelf books');
        }
        
        const data = await response.json();
        setBooks(data || []);
      } catch (error) {
        console.error('Error fetching shelf books:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchShelfBooks();
    }
  }, [isAuthenticated]);

  // Sort books based on selected criteria
  const sortedBooks = [...books].sort((a, b) => {
    switch (sortBy) {
      case 'title':
        return a.title.localeCompare(b.title);
      case 'author':
        return a.author.localeCompare(b.author);
      case 'dateAdded':
      default:
        return new Date(b.dateAdded) - new Date(a.dateAdded);
    }
  });

  const handleRemoveBook = async (bookId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/shelf/books/${bookId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to remove book');
      }

      // Update local state to remove the book
      setBooks(prevBooks => prevBooks.filter(book => book.bookId !== bookId));
    } catch (error) {
      console.error('Error removing book:', error);
    }
  };

  const handleManageSubscription = () => {
    setShowSubscriptionModal(true);
  };

  // Handle book modal
  const handleBookClick = (book) => {
    // Transform shelf book data to match BookModal expectations
    const modalBook = {
      ...book,
      explanation: book.description // Map description to explanation for BookModal
    };
    setSelectedBook(modalBook);
    setShowBookModal(true);
  };

  const handleCloseBookModal = () => {
    setShowBookModal(false);
    setSelectedBook(null);
  };

  // Don't render anything while checking authentication
  if (authLoading) {
    return (
      <div className="fixed inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-600 border-t-transparent"></div>
      </div>
    );
  }

  // Don't render if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  // Book Card component reused from RecommendationsView but modified for shelf
  const BookCard = ({ book }) => {
    return (
      <div className="relative flex flex-col md:flex-row gap-5 p-6 rounded-2xl bg-white shadow-sm hover:shadow-md transition-all border border-gray-100 cursor-pointer group"
           onClick={() => handleBookClick(book)}>
        
        {/* Delete button - positioned at top right */}
        <button 
          onClick={(e) => {
            e.stopPropagation(); // Prevent opening modal when clicking delete
            handleRemoveBook(book.bookId);
          }}
          className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center text-red-500 rounded-full hover:bg-red-50 transition-all duration-300 group-hover:opacity-100 opacity-50 bg-white/80 backdrop-blur-sm shadow-sm"
          aria-label="Remove from shelf"
        >
          <svg 
            className="w-4 h-4 transform transition-transform duration-300 hover:scale-110" 
            viewBox="0 0 24 24" 
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>

        {/* Book Image */}
        <div className="flex-shrink-0">
          <BookImage 
            src={book.thumbnail} 
            alt={book.title}
            className="w-20 h-28 sm:w-24 sm:h-32 rounded-lg shadow-sm"
          />
        </div>

        {/* Book Details */}
        <div className="flex-1 min-w-0">
          <div className="space-y-2">
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 font-fraunces leading-tight">
              {book.title}
            </h3>
            <p className="text-gray-600 font-space">
              by {book.author}
            </p>
            
            {book.description && (
              <p className="text-sm text-gray-600 font-space leading-relaxed line-clamp-3">
                {book.description}
              </p>
            )}

            {/* Amazon Link */}
            {book.amazonLink && (
              <a 
                href={book.amazonLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()} // Prevent opening modal when clicking link
                className="inline-flex items-center gap-2 text-sm text-purple-600 hover:text-purple-700 font-medium font-space transition-colors"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
                </svg>
                View on Amazon
              </a>
            )}
          </div>

          {/* Bottom section with genres only */}
          <div className="mt-3 pt-3 border-t border-gray-100">
            {/* Genre tags */}
            <div className="flex flex-wrap gap-2">
              {book.genres?.map(genre => (
                <span 
                  key={genre} 
                  className="px-2.5 py-0.5 bg-gradient-to-r from-purple-50/80 to-blue-50/80 text-purple-700 text-xs font-medium rounded-full border border-purple-100/50 font-space shadow-sm backdrop-blur-sm"
                >
                  {genre}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="relative min-h-screen">
      {/* Purple gradient background */}
      <div className="fixed inset-0 bg-gradient-to-br from-purple-100/80 via-white to-blue-100/80 -z-10" />

      <Navbar />

      {/* Main Content Layout */}
      <div className="pt-16">
        <div className="w-full max-w-7xl mx-auto flex flex-col lg:flex-row relative">
          {/* Desktop: Sticky Left Section - 30% */}
          <div className="hidden lg:block w-[30%] max-w-[360px]">
            <div className="sticky top-20 pt-12 px-8 pb-8 space-y-6">
              <div className="space-y-4">
                <h2 className="text-3xl font-medium text-gray-900 leading-tight font-fraunces">
                  Your Reading Shelf
                </h2>
                <p className="text-gray-600 text-base font-space leading-relaxed">
                  Keep track of your favorite books and discoveries.
                </p>
              </div>

              {/* Subscription Status Section */}
              <div className="bg-white rounded-xl p-4 border border-gray-200 space-y-3">
                <h3 className="text-sm font-semibold text-gray-900 font-space">Subscription Status</h3>
                
                {subscription?.status === 'active' ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-sm text-gray-700 font-space">PRO Active</span>
                    </div>
                    {subscription.cancelAtPeriodEnd && (
                      <p className="text-xs text-orange-600 font-space">
                        Cancels at period end
                      </p>
                    )}
                    <button
                      onClick={handleManageSubscription}
                      className="w-full px-3 py-2 text-xs bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-space"
                    >
                      Manage Subscription
                    </button>
                  </div>
                ) : subscription?.status === 'canceled' ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      <span className="text-sm text-gray-700 font-space">Subscription Canceled</span>
                    </div>
                    <p className="text-xs text-gray-600 font-space">
                      You can upgrade anytime to get unlimited access.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                      <span className="text-sm text-gray-700 font-space">Free Plan</span>
                    </div>
                    <p className="text-xs text-gray-600 font-space">
                      Upgrade to PRO for 15 recommendations per day.
                    </p>
                  </div>
                )}
              </div>

              {/* Sort options */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700 font-space">Sort by</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-3 py-2 bg-white rounded-lg border border-gray-200 text-gray-700 text-sm font-space focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="dateAdded">Date Added</option>
                  <option value="title">Title</option>
                  <option value="author">Author</option>
                </select>
              </div>
            </div>
          </div>

          {/* Mobile: Sticky Title Section */}
          <div className="lg:hidden sticky top-16 z-40">
            <div className="py-6 bg-white/80 backdrop-blur-md border-b border-purple-100/30">
              <div className="px-4 space-y-3">
                <h2 className="text-2xl font-fraunces">Your Reading Shelf</h2>
                
                {/* Mobile Subscription Status */}
                <div className="bg-white rounded-lg p-3 border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {subscription?.status === 'active' ? (
                        <>
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="text-sm text-gray-700 font-space">PRO Active</span>
                        </>
                      ) : (
                        <>
                          <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                          <span className="text-sm text-gray-700 font-space">Free Plan</span>
                        </>
                      )}
                    </div>
                    {subscription?.status === 'active' && (
                      <button
                        onClick={handleManageSubscription}
                        className="text-xs text-purple-600 hover:text-purple-700 font-space"
                      >
                        Manage
                      </button>
                    )}
                  </div>
                </div>
                
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-3 py-2 bg-white rounded-lg border border-gray-200 text-gray-700 text-sm font-space focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="dateAdded">Sort by Date Added</option>
                  <option value="title">Sort by Title</option>
                  <option value="author">Sort by Author</option>
                </select>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="w-full lg:w-[70%] lg:ml-[10%]">
            <div className="max-w-3xl py-4 sm:py-12 px-4 sm:px-8 pb-20">
              {isLoading ? (
                // Loading state
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-purple-600 border-t-transparent"></div>
                </div>
              ) : books.length === 0 ? (
                // Empty state
                <div className="text-center py-12 space-y-4">
                  <div className="w-16 h-16 mx-auto text-purple-300">
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
                    </svg>
                  </div>
                  <h3 className="text-xl font-medium text-gray-900 font-fraunces">Your shelf is empty</h3>
                  <p className="text-gray-600 font-space">Start adding books from recommendations to build your collection.</p>
                </div>
              ) : (
                // Book list
                <div className="space-y-4">
                  {sortedBooks.map((book) => (
                    <BookCard key={book.bookId} book={book} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Subscription Management Modal */}
      <SubscriptionManagementModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
      />

      {/* Book Details Modal */}
      <BookModal
        book={selectedBook}
        isOpen={showBookModal}
        onClose={handleCloseBookModal}
      />
    </div>
  );
}

export default ShelfPage; 